import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Link2, Trash2 } from "lucide-react"
import { useActiveOrg } from "@/core/active-event"
import {
  buildInviteUrl,
  cancelInvitation,
  inviteToClient,
  listClientMembers,
  removeMember,
  setInviteUserName,
  updateMemberRole,
  type OrgInvitation,
  type OrgMember,
} from "@/core/admin"
import {
  Card,
  GhostButton,
  Pill,
  PrimaryButton,
  Select,
  SettingsPage,
  TextInput,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/settings/people")({
  component: OrgPeople,
})

type Role = "owner" | "admin" | "member"
const ROLES: Role[] = ["member", "admin", "owner"]

function OrgPeople() {
  const { organizationId } = useActiveOrg()
  const orgId = organizationId ?? ""
  const qc = useQueryClient()
  const key = ["org-members", organizationId]

  const q = useQuery({
    queryKey: key,
    queryFn: () => listClientMembers(orgId),
    enabled: !!organizationId,
  })

  const refresh = () => void qc.invalidateQueries({ queryKey: key })
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong")

  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [role, setRole] = useState<Role>("member")

  const invite = useMutation({
    mutationFn: async () => {
      const e = email.trim()
      const inv = await inviteToClient({ organizationId: orgId, email: e, role })
      // Provision a passwordless Better Auth account (email_verified=false) for
      // the invitee right away. Without an account, `disableSignUp` means the
      // email-OTP sign-in code is never sent (no account → no code), so the
      // invitee can't log in until they accept. Creating the account here lets
      // them request a sign-in code immediately. Best-effort: setInviteUserName
      // swallows its own errors so it never blocks the invite. Falls back to the
      // email's local part when no display name was entered.
      await setInviteUserName(e, name.trim() || e.split("@")[0])
      return inv
    },
    onSuccess: () => {
      toast.success(`Invited ${email.trim()}`)
      setEmail("")
      setName("")
      setRole("member")
      refresh()
    },
    onError,
  })

  const setRoleM = useMutation({
    mutationFn: (v: { memberId: string; role: Role }) =>
      updateMemberRole({ organizationId: orgId, memberId: v.memberId, role: v.role }),
    onSuccess: () => {
      toast.success("Role updated")
      refresh()
    },
    onError,
  })

  const remove = useMutation({
    mutationFn: (memberId: string) => removeMember({ organizationId: orgId, memberIdOrEmail: memberId }),
    onSuccess: () => {
      toast.success("Member removed")
      refresh()
    },
    onError,
  })

  const cancel = useMutation({
    mutationFn: (invitationId: string) => cancelInvitation(invitationId),
    onSuccess: () => {
      toast.success("Invitation cancelled")
      refresh()
    },
    onError,
  })

  const members = q.data?.members ?? []
  const invites = q.data?.invitations ?? []
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())

  return (
    <SettingsPage
      title="Members & invites"
      description="People who can manage this organization and its events."
    >
      <Card title="Invite a member">
        <form
          className="flex flex-col gap-2.5 @[520px]:flex-row @[520px]:items-end"
          onSubmit={(e) => {
            e.preventDefault()
            if (emailValid && !invite.isPending) invite.mutate()
          }}
        >
          <div className="flex-1 space-y-1.5">
            <label className="block text-[12.5px] font-semibold text-plum-800 dark:text-plum-100">
              Email
            </label>
            <TextInput
              type="email"
              value={email}
              placeholder="name@company.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="block text-[12.5px] font-semibold text-plum-800 dark:text-plum-100">
              Name <span className="font-normal text-plum-700/45 dark:text-plum-200/35">(optional)</span>
            </label>
            <TextInput
              type="text"
              value={name}
              placeholder="Cameron Cheller"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-[12.5px] font-semibold text-plum-800 dark:text-plum-100">
              Role
            </label>
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {cap(r)}
                </option>
              ))}
            </Select>
          </div>
          <PrimaryButton type="submit" disabled={!emailValid || invite.isPending}>
            {invite.isPending ? "Inviting…" : "Send invite"}
          </PrimaryButton>
        </form>
      </Card>

      <Card title={`Members${members.length ? ` (${members.length})` : ""}`}>
        {q.isLoading ? (
          <ul className="divide-y divide-plum-200/50 dark:divide-white/10">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center gap-3 py-3">
                <span className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-plum-100 dark:bg-white/10" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <span className="block h-3.5 w-40 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                  <span className="block h-3 w-52 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                </div>
                <span className="h-8 w-[110px] shrink-0 animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
                <span className="h-8 w-8 shrink-0 animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
              </li>
            ))}
          </ul>
        ) : q.isError ? (
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load members"}
          </p>
        ) : members.length === 0 ? (
          <p className="text-[13px] text-plum-700/55 dark:text-plum-200/40">No members yet.</p>
        ) : (
          <ul className="divide-y divide-plum-200/50 dark:divide-white/10">
            {members.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                onRole={(r) => setRoleM.mutate({ memberId: m.id, role: r })}
                onRemove={() => {
                  if (confirm(`Remove ${m.user.email} from this organization?`)) remove.mutate(m.id)
                }}
                busy={setRoleM.isPending || remove.isPending}
              />
            ))}
          </ul>
        )}
      </Card>

      {invites.length > 0 && (
        <Card title={`Pending invitations (${invites.length})`}>
          <ul className="divide-y divide-plum-200/50 dark:divide-white/10">
            {invites.map((inv) => (
              <InviteRow
                key={inv.id}
                invite={inv}
                onCancel={() => cancel.mutate(inv.id)}
                busy={cancel.isPending}
              />
            ))}
          </ul>
        </Card>
      )}
    </SettingsPage>
  )
}

function MemberRow({
  member,
  onRole,
  onRemove,
  busy,
}: {
  member: OrgMember
  onRole: (role: Role) => void
  onRemove: () => void
  busy: boolean
}) {
  return (
    <li className="flex items-center gap-3 py-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-amber-300 to-orange-500 text-[12px] font-bold text-plum-950">
        {initials(member.user.name, member.user.email)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
          {member.user.name?.trim() || member.user.email}
        </p>
        {member.user.name?.trim() && (
          <p className="truncate text-[12px] text-plum-700/55 dark:text-plum-200/40">
            {member.user.email}
          </p>
        )}
      </div>
      <Select
        className="h-8 w-[110px]"
        value={member.role}
        disabled={busy}
        onChange={(e) => onRole(e.target.value as Role)}
      >
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {cap(r)}
          </option>
        ))}
      </Select>
      <button
        type="button"
        title="Remove member"
        disabled={busy}
        onClick={onRemove}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-plum-700/45 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50 dark:text-plum-200/35 dark:hover:bg-rose-500/10 dark:hover:text-rose-400"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  )
}

function InviteRow({
  invite,
  onCancel,
  busy,
}: {
  invite: OrgInvitation
  onCancel: () => void
  busy: boolean
}) {
  const copyLink = () => {
    void navigator.clipboard
      .writeText(buildInviteUrl(invite.id))
      .then(() => toast.success("Invite link copied"))
      .catch(() => toast.error("Couldn't copy link"))
  }
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-plum-900 dark:text-plum-50">
          {invite.email}
        </p>
        <p className="text-[11.5px] text-plum-700/50 dark:text-plum-200/35">
          Expires {formatDate(invite.expiresAt)}
        </p>
      </div>
      <Pill>{cap(invite.role)}</Pill>
      <GhostButton className="h-8 px-2.5" onClick={copyLink} title="Copy invite link">
        <Link2 className="h-3.5 w-3.5" />
      </GhostButton>
      <GhostButton
        className="h-8 px-3 text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
        disabled={busy}
        onClick={onCancel}
      >
        Cancel
      </GhostButton>
    </li>
  )
}

function cap(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s
}

function initials(name: string | null, email: string): string {
  const n = name?.trim()
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
  }
  return email.slice(0, 2).toUpperCase()
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
}
